// Le bouton « Partager » — discret, au clavier, et jamais sur un contenu verrouillé.
//
// Il se pose à côté de l'épingle, dans le coin de la tuile ou de la carte : c'est déjà
// l'emplacement des actions de l'observatoire, et le mini-CDC E de juillet le plaçait
// exactement là (« au survol d'un graphique : icône Partager, à côté des actions existantes
// pin / télécharger »). Ce qui a changé le 22/09, c'est ce qu'il envoie — une image, plus
// un lien — pas où il se trouve.
//
// DEUX FORMATS DEPUIS LE 30/09 (Marc) : le PNG, pour une messagerie, et le SVG, vectoriel,
// pour un rapport ou une présentation. Là où le SVG existe (tuiles, graphiques), le bouton
// ouvre un petit choix de format ; là où il n'existe pas (cartes : un fond de carte est une
// image, voir `imageSvg.ts`), il garde son geste direct — un clic, un PNG.
//
// LE LIEN DEPUIS LE 03/10 (Marc, CDC E2) : sur un graphique de la commune abonnée, le choix
// gagne une troisième entrée, « Lien (14 jours) », qui ouvre la fenêtre de création
// (`FenetreLien`). Le partage en image est inchangé.
//
// Il reste ATTEIGNABLE sans souris : l'opacité tombe au repos, jamais la place ni le focus.
import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { FocusEvent, KeyboardEvent, MouseEvent } from 'react'
import { Share2 } from 'lucide-react'

import FenetreLien from './FenetreLien'
import type { LienCree } from './lien'
import { nomFichierPartage, type FormatImage } from './nom'
import { partagerImage, telecharger } from './partager'
import './partage.css'

export interface PartageMeta {
  /** Nom de la commune — en tête de l'image, et dans le nom du fichier. */
  commune: string
}

export interface BoutonPartageProps {
  /** Intitulé de ce qu'on partage : il nomme le bouton et le fichier. */
  titre: string
  partage: PartageMeta
  /** Fabrique le PNG. Paresseux : on ne compose rien tant que personne n'a cliqué.
   *  Reçoit le bouton lui-même — c'est par lui que le graphique retrouve son canvas,
   *  sans qu'on ait à faire descendre une ref à travers `Explicable`. */
  composer: (bouton: HTMLButtonElement) => Promise<Blob>
  /** Fabrique le SVG (30/09). Absent = PNG seul, en un clic, sans choix de format. */
  composerSvg?: (bouton: HTMLButtonElement) => Promise<Blob>
  /** Partage par lien (03/10, CDC E2) : présent seulement quand le compte peut créer un lien
   *  pour ce graphique. Ne reçoit rien du graphique : le serveur le reconstruit lui-même. */
  lien?: { creer: () => Promise<LienCree> }
  className?: string
}

type Etat = 'pret' | 'travail' | 'echec'

export default function BoutonPartage({ titre, partage, composer, composerSvg, lien, className }:
                                      BoutonPartageProps) {
  const [etat, setEtat] = useState<Etat>('pret')
  const [choix, setChoix] = useState(false)
  const [fenetreLien, setFenetreLien] = useState(false)
  const aChoix = !!composerSvg || !!lien
  const bouton = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const idMenu = useId()
  // Le composant peut disparaître pendant la composition (changement de thématique) :
  // poser l'état sur un composant démonté déclencherait un avertissement React et,
  // surtout, masquerait la vraie erreur.
  const monte = useRef(true)
  useEffect(() => () => { monte.current = false }, [])

  // Le choix ouvert, le focus y entre (on n'a pas à le chercher au clavier) ; un clic
  // ailleurs dans la page le referme, comme tout menu.
  useEffect(() => {
    if (!choix) return
    menu.current?.querySelector('button')?.focus()
    const dehors = (e: globalThis.MouseEvent) => {
      const cible = e.target as Node | null
      if (cible && (menu.current?.contains(cible) || bouton.current?.contains(cible))) return
      setChoix(false)
    }
    document.addEventListener('mousedown', dehors)
    return () => document.removeEventListener('mousedown', dehors)
  }, [choix])

  const fermerChoix = () => {
    setChoix(false)
    // Le menu disparaît avec l'élément qui avait le focus : sans ce retour, le focus
    // retomberait en tête de document.
    bouton.current?.focus()
  }

  const exporter = async (format: FormatImage) => {
    const b = bouton.current
    if (!b || etat === 'travail') return
    setEtat('travail')
    try {
      if (format === 'svg' && composerSvg) {
        const svg = await composerSvg(b)
        // Le SVG se TÉLÉCHARGE : c'est un fichier de travail (rapport, présentation), et
        // la plupart des messageries n'en affichent même pas l'aperçu.
        telecharger(svg, nomFichierPartage(partage.commune, titre, 'svg'))
      } else {
        const png = await composer(b)
        await partagerImage(png, nomFichierPartage(partage.commune, titre, 'png'),
                            `${titre} — ${partage.commune}`)
      }
      if (monte.current) setEtat('pret')
    } catch {
      // Un bouton de confort ne casse pas la page : on le dit, on n'escalade pas.
      if (monte.current) setEtat('echec')
    }
  }

  const cliquer = (e: MouseEvent) => {
    // La tuile vit dans des conteneurs qui écoutent les clics (la carte épinglée glissable
    // de la synthèse) : le geste s'arrête au bouton qui l'a reçu.
    e.preventDefault()
    e.stopPropagation()
    if (etat === 'travail') return
    if (aChoix) setChoix(c => !c)
    else void exporter('png')
  }

  const ouvrirLien = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setChoix(false)
    setFenetreLien(true)
  }
  const fermerLien = () => {
    setFenetreLien(false)
    bouton.current?.focus()
  }

  const choisir = (format: FormatImage) => (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    fermerChoix()
    void exporter(format)
  }

  const toucheMenu = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      fermerChoix()
      return
    }
    // Flèches haut / bas entre les deux formats : le réflexe clavier d'une liste de choix.
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    const options = [...(menu.current?.querySelectorAll('button') ?? [])]
    const i = options.indexOf(document.activeElement as HTMLButtonElement)
    if (!options.length) return
    e.preventDefault()
    const suivant = e.key === 'ArrowDown' ? (i + 1) % options.length
                                          : (i - 1 + options.length) % options.length
    options[suivant].focus()
  }

  // Le focus qui quitte le choix (Tabulation) le referme : un menu ouvert derrière soi
  // resterait posé sur la tuile voisine.
  const sortieMenu = (e: FocusEvent<HTMLDivElement>) => {
    const vers = e.relatedTarget as Node | null
    if (vers && (menu.current?.contains(vers) || bouton.current?.contains(vers))) return
    setChoix(false)
  }

  return (
    <>
      <button ref={bouton} type="button"
        className={`obs2-partage${className ? ` ${className}` : ''}`}
        // Un nom accessible générique répété quinze fois par page rend la liste des boutons
        // d'un lecteur d'écran inutilisable : le nom porte l'indicateur.
        aria-label={lien ? `Partager l’image ou un lien : ${titre}` : `Partager l’image : ${titre}`}
        title={lien ? 'Partager l’image (PNG, SVG) ou un lien de 14 jours'
          : composerSvg ? 'Partager ou exporter l’image (PNG, SVG)' : 'Partager l’image'}
        aria-expanded={aChoix ? choix : undefined}
        aria-controls={aChoix && choix ? idMenu : undefined}
        // `aria-disabled` et non `disabled` pendant la composition : un bouton désactivé
        // perd le focus (règle de « focus fixup »), et le lecteur au clavier repartirait
        // du haut de la page. `cliquer` ignore le geste tant que la composition dure.
        aria-busy={etat === 'travail'}
        aria-disabled={etat === 'travail' || undefined}
        onClick={cliquer}
        onKeyDown={e => { if (e.key === 'Escape' && choix) { e.preventDefault(); fermerChoix() } }}>
        <Share2 size={13} aria-hidden="true" />
      </button>
      {aChoix && choix && (
        <div ref={menu} id={idMenu} className="obs2-partage-menu" role="group"
          aria-label={lien ? `Format du partage : ${titre}` : `Format de l’image : ${titre}`}
          onKeyDown={toucheMenu} onBlur={sortieMenu}
          // Safari (macOS) ne donne pas le focus à un bouton cliqué : il RETIRE le focus de
          // l'option active, `sortieMenu` refermerait le choix, et le clic tomberait dans le
          // vide. Empêcher le changement de focus au `mousedown` garde le clic intact.
          onMouseDown={e => e.preventDefault()}>
          <button type="button" onClick={choisir('png')}>
            Image PNG <span className="obs2-partage-menu-aide">à partager</span>
          </button>
          {composerSvg && (
            <button type="button" onClick={choisir('svg')}>
              Image SVG <span className="obs2-partage-menu-aide">vectorielle, pour un rapport</span>
            </button>
          )}
          {lien && (
            <button type="button" onClick={ouvrirLien}>
              Lien <span className="obs2-partage-menu-aide">valable 14 jours, sans compte</span>
            </button>
          )}
        </div>
      )}
      {/* Hors de la carte (portail) : la fenêtre ne doit hériter ni du contexte d'empilement ni
          d'un éventuel découpage de la tuile qui la déclenche. */}
      {/* `obs2-jetons` : hors de `.obs2`, la fenêtre doit porter les couleurs de la charte (le bouton
          principal était blanc sur blanc, 04/10). */}
      {lien && fenetreLien && createPortal(
        <div className="obs2-jetons">
          <FenetreLien titre={titre} commune={partage.commune} creer={lien.creer} onClose={fermerLien} />
        </div>,
        document.body)}
      {etat === 'echec' && (
        <span className="obs2-partage-echec" role="status">
          L’image n’a pas pu être produite.
        </span>
      )}
    </>
  )
}
